import { ClientMsg, ServerMsg } from "@neon-purge/shared";
import type { Room } from "colyseus.js";
import { audio } from "./audio.js";

/**
 * VOICE CHAT transport — a process-wide SINGLETON so the mic and the peer mesh
 * survive lobby↔match transitions. Each screen just `attach()`es the current
 * room; the mic stream is captured ONCE and kept alive across attaches, so
 * turning it on in the lobby carries into the game (and back) with no gap and
 * no need to toggle off/on.
 *
 * Topology: a directional WebRTC publish mesh. While my mic is on I keep one
 * RTCPeerConnection per other human in the room and push my mic track down it;
 * they answer with a receive-only peer and play the remote stream in a hidden
 * <audio>. Signaling rides the game socket via VoiceSignal (server relays it).
 * STUN only — symmetric-NAT pairs may fail (you just don't hear that one).
 */
const RTC_CONFIG: RTCConfiguration = {
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
};

type Signal =
  | { type: "offer"; sdp: string }
  | { type: "answer"; sdp: string }
  | { type: "ice"; pub: boolean; cand: RTCIceCandidateInit }
  | { type: "bye" };

type SinkableMedia = HTMLMediaElement & { setSinkId?: (sinkId: string) => Promise<void> };

class VoiceChat {
  private room: Room | null = null;
  private unbind: (() => void) | null = null;
  /** My mic → their ears. */
  private pub = new Map<string, RTCPeerConnection>();
  /** Their mic → my ears. */
  private recv = new Map<string, RTCPeerConnection>();
  private audioEls = new Map<string, HTMLAudioElement>();
  private stream: MediaStream | null = null;
  private hb = 0;
  /** A getUserMedia call already in flight (double-toggle guard). */
  private pending: Promise<boolean> | null = null;
  /** True when the last capture attempt was denied by the browser/user. */
  blocked = false;

  constructor() {
    const refresh = () => this.preferDefaultOutput();
    window.addEventListener("gamepadconnected", refresh);
    window.addEventListener("gamepaddisconnected", refresh);
    document.addEventListener("visibilitychange", () => { if (!document.hidden) refresh(); });
  }

  /** Is the mic currently live? */
  get on(): boolean { return !!this.stream; }

  /** Point voice at the room for the current screen. Idempotent for the same
   *  room. Switching rooms rebinds signaling and rebuilds peers WITHOUT
   *  dropping the mic stream (seamless lobby↔match). */
  attach(room: Room): void {
    if (this.room === room) return;
    this.unbindRoom(); // closes peers keyed to the old session ids
    this.room = room;
    this.unbind = room.onMessage(ServerMsg.VoiceSignal, (msg: { from: string; data: Signal }) => {
      void this.onSignal(msg.from, msg.data);
    });
    if (!this.hb) {
      this.hb = window.setInterval(() => this.tick(), 2000);
    }
    // re-offer to everyone in the new room immediately (don't wait a heartbeat)
    if (this.stream) for (const sid of this.others()) void this.ensurePub(sid);
  }

  /** Turn the mic ON: capture once (kept across attaches) and publish. */
  async enable(): Promise<boolean> {
    if (this.stream) { audio.micWanted = true; this.pushToAll(); return true; }
    // share an in-flight capture: two rapid toggles (M key + button, or the
    // lobby auto-enable racing a manual click) must not BOTH call getUserMedia
    // — the loser's live stream would be orphaned with the mic held hot
    if (this.pending) return this.pending;
    audio.micWanted = true; // declared intent — disable() clears it mid-flight
    this.pending = (async (): Promise<boolean> => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) return false;
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // the user toggled OFF (or we detached) while the permission prompt
        // was open — release the grant instead of orphaning a hot mic
        if (!audio.micWanted || this.stream) {
          stream.getTracks().forEach((t) => t.stop());
          return !!this.stream;
        }
        this.blocked = false;
        this.stream = stream;
        this.pushToAll();
        return true;
      } catch {
        this.blocked = true;
        return false;
      } finally {
        this.pending = null;
      }
    })();
    return this.pending;
  }

  /** Turn the mic OFF: stop capturing and tell peers to drop us. */
  disable(): void {
    audio.micWanted = false;
    for (const [sid, pc] of this.pub) { this.signal(sid, { type: "bye" }); pc.close(); }
    this.pub.clear();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  async toggle(): Promise<boolean> {
    if (this.stream) { this.disable(); return false; }
    return this.enable();
  }

  /** Leaving to the menu (no room): stop everything, but keep micWanted so the
   *  next room re-enables it. */
  detach(): void {
    this.unbindRoom();
    if (this.hb) { window.clearInterval(this.hb); this.hb = 0; }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  // -------------------------------------------------------------- internals

  private pushToAll(): void {
    if (!this.stream) return;
    for (const sid of this.others()) void this.ensurePub(sid);
  }

  private tick(): void {
    const live = new Set(this.others());
    // drop peers whose target left OR whose connection died, so ensurePub
    // rebuilds a FRESH offer — this is what recovers voice after someone
    // refreshes and rejoins with a new session id (the old peer went stale).
    for (const [sid, pc] of this.pub) {
      const dead = pc.connectionState === "failed" || pc.connectionState === "disconnected" || pc.connectionState === "closed";
      if (!live.has(sid) || dead) { pc.close(); this.pub.delete(sid); }
    }
    for (const [sid, pc] of this.recv) {
      const dead = pc.connectionState === "failed" || pc.connectionState === "closed";
      if (!live.has(sid) || dead) this.dropRecv(sid);
    }
    if (this.stream) for (const sid of live) void this.ensurePub(sid);
    // autoplay may have paused an incoming voice stream — nudge it back
    for (const el of this.audioEls.values()) {
      this.routeAudio(el);
      if (el.paused) void el.play().catch(() => { /* needs a gesture */ });
    }
  }

  private unbindRoom(): void {
    try { this.unbind?.(); } catch { /* already unbound */ }
    this.unbind = null;
    for (const [sid, pc] of this.pub) { this.signal(sid, { type: "bye" }); pc.close(); }
    this.pub.clear();
    for (const sid of [...this.recv.keys()]) this.dropRecv(sid);
    this.room = null;
  }

  private others(): string[] {
    const out: string[] = [];
    const room = this.room;
    if (!room) return out;
    const players = (room.state as { players?: Map<string, unknown> }).players;
    players?.forEach?.((_q: unknown, sid: string) => {
      if (sid !== room.sessionId && !sid.startsWith("bot")) out.push(sid);
    });
    return out;
  }

  private signal(to: string, data: Signal): void {
    try { this.room?.send(ClientMsg.VoiceSignal, { to, data }); } catch { /* room gone */ }
  }

  private async ensurePub(sid: string): Promise<void> {
    if (!this.stream || !this.room || this.pub.has(sid)) return;
    const pc = new RTCPeerConnection(RTC_CONFIG);
    this.pub.set(sid, pc);
    for (const t of this.stream.getTracks()) pc.addTrack(t, this.stream);
    pc.onicecandidate = (e) => { if (e.candidate) this.signal(sid, { type: "ice", pub: true, cand: e.candidate.toJSON() }); };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" || pc.connectionState === "closed") {
        if (this.pub.get(sid) === pc) this.pub.delete(sid);
      }
    };
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.signal(sid, { type: "offer", sdp: offer.sdp ?? "" });
    } catch { pc.close(); this.pub.delete(sid); }
  }

  private async onSignal(from: string, data: Signal): Promise<void> {
    if (!data || !this.room) return;
    try {
      if (data.type === "offer") {
        this.dropRecv(from);
        const pc = new RTCPeerConnection(RTC_CONFIG);
        this.recv.set(from, pc);
        pc.onicecandidate = (e) => { if (e.candidate) this.signal(from, { type: "ice", pub: false, cand: e.candidate.toJSON() }); };
        pc.ontrack = (e) => this.attachAudio(from, e.streams[0] ?? new MediaStream([e.track]));
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === "failed" || pc.connectionState === "closed") {
            if (this.recv.get(from) === pc) this.dropRecv(from);
          }
        };
        await pc.setRemoteDescription({ type: "offer", sdp: data.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.signal(from, { type: "answer", sdp: answer.sdp ?? "" });
      } else if (data.type === "answer") {
        await this.pub.get(from)?.setRemoteDescription({ type: "answer", sdp: data.sdp });
      } else if (data.type === "ice") {
        const pc = data.pub ? this.recv.get(from) : this.pub.get(from);
        await pc?.addIceCandidate(data.cand);
      } else if (data.type === "bye") {
        this.dropRecv(from);
      }
    } catch { /* signaling race — heartbeat recovers */ }
  }

  private attachAudio(sid: string, stream: MediaStream): void {
    let el = this.audioEls.get(sid);
    if (!el) {
      el = document.createElement("audio");
      el.autoplay = true;
      el.setAttribute("playsinline", "");
      el.style.display = "none";
      this.routeAudio(el);
      document.body.appendChild(el);
      this.audioEls.set(sid, el);
    }
    el.srcObject = stream;
    void el.play().catch(() => { /* autoplay-gated — resumes on next gesture */ });
  }

  private dropRecv(sid: string): void {
    this.recv.get(sid)?.close();
    this.recv.delete(sid);
    const el = this.audioEls.get(sid);
    if (el) { el.srcObject = null; el.remove(); this.audioEls.delete(sid); }
  }

  private preferDefaultOutput(): void {
    for (const el of this.audioEls.values()) this.routeAudio(el);
  }

  private routeAudio(el: HTMLMediaElement): void {
    void (el as SinkableMedia).setSinkId?.("").catch(() => { /* no output routing API */ });
  }
}

/** Process-wide voice singleton — attach the current room, toggle the mic. */
export const voiceChat = new VoiceChat();
